import React, { useState, useEffect, useMemo } from 'react';
import {
  TextField, Button, Box, Typography, IconButton,
  InputLabel, FormControl, Select, MenuItem, alpha, Grid, Divider,
  Chip, FormHelperText, Stack, Paper, CircularProgress
} from '@mui/material';
import { Plus, Trash2, Globe, Sparkles, Layers, Tag, AlignLeft, Info } from 'lucide-react';
import axios from '../utils/axios';
import { AdminDialog, AdminDialogHeader, AdminDialogContent, AdminDialogActions } from './common/modal';

const CustomPageModal = ({
  open,
  onClose,
  pageData,
  prefillData,
  categories = [],
  subCategories = [],
  brands = [],
  cities = [],
  onSuccess
}) => {
  const isEdit = !!pageData;

  const BLANK_FORM = {
    page_name: '',
    slug: '',
    category: '',
    sub_category: '',
    brand: '',
    city: '',
    buy_sell: 'buy',
    meta_title: '',
    meta_description: '',
    keywords: '',
    meta_content: []
  };

  const [formData, setFormData] = useState(BLANK_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // ── Initialize form on open ─────────────────────────────────────────────
  useEffect(() => {
    if (pageData && open) {
      setFormData({
        page_name: pageData.page_name || '',
        slug: pageData.slug || '',
        category: pageData.category?._id || pageData.category || '',
        sub_category: pageData.sub_category || '',
        brand: pageData.brand || '',
        city: pageData.city || '',
        buy_sell: pageData.buy_sell || 'buy',
        meta_title: pageData.meta_title || '',
        meta_description: pageData.meta_description || '',
        keywords: pageData.keywords || '',
        meta_content: pageData.meta_content || []
      });
    } else if (open) {
      setFormData({ ...BLANK_FORM, ...(prefillData || {}) });
    }
    setError('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData, prefillData, open]);

  // ── Filter subcategories based on selected category ─────────────────────
  const availableSubCategories = useMemo(() => {
    if (!formData.category) return [];
    return subCategories.filter((sub) => {
      const catId = sub.category_id?._id || sub.category_id;
      return String(catId) === String(formData.category);
    });
  }, [subCategories, formData.category]);

  // ── Filter brands based on selected category ────────────────────────────
  const availableBrands = useMemo(() => {
    if (!formData.category) return [];
    return brands.filter((b) => {
      const catId = b.category_id?._id || b.category_id;
      return String(catId) === String(formData.category);
    });
  }, [brands, formData.category]);

  // ── Slugify helper ──────────────────────────────────────────────────────
  const generateSlug = (text) => {
    return String(text || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (error) setError('');

    if (name === 'category') {
      // When category changes, reset dependent sub_category & brand
      setFormData((prev) => ({
        ...prev,
        category: value,
        sub_category: '',
        brand: ''
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleAutoSlug = () => {
    if (formData.page_name) {
      setFormData((prev) => ({
        ...prev,
        slug: generateSlug(prev.page_name)
      }));
    }
  };

  // ── Meta Content array handlers ─────────────────────────────────────────
  const handleMetaContentChange = (index, field, value) => {
    const newMetaContent = formData.meta_content.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setFormData((prev) => ({ ...prev, meta_content: newMetaContent }));
  };

  const addMetaContent = () => {
    setFormData((prev) => ({
      ...prev,
      meta_content: [...prev.meta_content, { keyword_title: '', keyword_description: '' }]
    }));
  };

  const removeMetaContent = (index) => {
    setFormData((prev) => ({
      ...prev,
      meta_content: prev.meta_content.filter((_, i) => i !== index)
    }));
  };

  // ── Submit handler ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.page_name?.trim() || !formData.slug?.trim() || !formData.category) {
      setError('Page Name, Slug, and Category are required');
      return;
    }

    setLoading(true);
    try {
      if (isEdit) {
        await axios.put(`/buy-and-sell/${pageData._id}`, formData, { skipNotify: 'success' });
      } else {
        await axios.post('/buy-and-sell', formData, { skipNotify: 'success' });
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.msg || 'Something went wrong while saving the page');
    } finally {
      setLoading(false);
    }
  };

  const selectedCategoryName = categories.find((c) => c._id === formData.category)?.category_name || '';

  return (
    <AdminDialog
      open={open}
      onClose={!loading ? onClose : undefined}
      maxWidth="md"
    >
      <AdminDialogHeader
        title={isEdit ? 'Edit Custom SEO Page' : 'Create Custom SEO Page'}
        subtitle="Configure search engine landing pages, targeting filters, and rich metadata"
        icon={Globe}
        onClose={!loading ? onClose : undefined}
      />

      <form onSubmit={handleSubmit}>
        <AdminDialogContent>
          {error && (
            <Box
              sx={{
                p: 2,
                mb: 3,
                borderRadius: 2,
                bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                border: (theme) => `1px solid ${alpha(theme.palette.error.main, 0.25)}`,
                color: 'error.dark',
                fontSize: '0.875rem',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5
              }}
            >
              <Info size={18} />
              <Typography variant="body2" fontWeight={600}>{error}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
            {/* ── Section 1: Page Identity & Targeting ──────────────────── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <Layers size={18} color="#2065D1" />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                  Page Identity & Category Targeting
                </Typography>
              </Box>

              <Grid container spacing={2.5}>
                {/* Page Name */}
                <Grid size={{ xs: 12, sm: 8 }}>
                  <TextField
                    label="Page Name"
                    name="page_name"
                    value={formData.page_name}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="e.g. Buy Laptops in Ahmedabad"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Buy / Sell Type */}
                <Grid size={{ xs: 12, sm: 4 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="buy_sell"
                      value={formData.buy_sell}
                      label="Type"
                      onChange={handleChange}
                      sx={{ borderRadius: 2 }}
                    >
                      <MenuItem value="buy">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip size="small" label="BUY" sx={{ height: 20, fontWeight: 700, fontSize: 10, bgcolor: alpha('#22c55e', 0.15), color: '#16a34a' }} />
                          <Typography variant="body2" fontWeight={600}>Buy Page</Typography>
                        </Box>
                      </MenuItem>
                      <MenuItem value="sell">
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip size="small" label="SELL" sx={{ height: 20, fontWeight: 700, fontSize: 10, bgcolor: alpha('#ef4444', 0.15), color: '#dc2626' }} />
                          <Typography variant="body2" fontWeight={600}>Sell Page</Typography>
                        </Box>
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                {/* URL Slug with Auto Slug generator */}
                <Grid size={12}>
                  <TextField
                    label="URL Slug"
                    name="slug"
                    value={formData.slug}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="e.g. buy-laptops-in-ahmedabad"
                    helperText={`Public URL: /${formData.buy_sell || 'buy'}/${formData.slug || 'url-slug'}`}
                    InputProps={{
                      endAdornment: (
                        <Button
                          size="small"
                          onClick={handleAutoSlug}
                          startIcon={<Sparkles size={14} />}
                          sx={{ textTransform: 'none', fontWeight: 700, fontSize: '0.75rem', whiteSpace: 'nowrap' }}
                        >
                          Auto Slug
                        </Button>
                      )
                    }}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Category Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>Category</InputLabel>
                    <Select
                      name="category"
                      value={formData.category}
                      label="Category"
                      onChange={handleChange}
                      sx={{ borderRadius: 2 }}
                    >
                      {categories.map((cat) => (
                        <MenuItem key={cat._id} value={cat._id}>
                          {cat.category_name}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>Select parent category to filter subcategories and brands</FormHelperText>
                  </FormControl>
                </Grid>

                {/* City Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth required>
                    <InputLabel>City</InputLabel>
                    <Select
                      name="city"
                      value={formData.city}
                      label="City"
                      onChange={handleChange}
                      sx={{ borderRadius: 2 }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      {cities.map((c) => (
                        <MenuItem key={c._id} value={c.city_name}>
                          {c.city_name}
                        </MenuItem>
                      ))}
                      {/* Allow custom city fallback if city is not in standard list */}
                      {formData.city && !cities.some((c) => c.city_name.toLowerCase() === formData.city.toLowerCase()) && (
                        <MenuItem value={formData.city}>
                          {formData.city} (Custom)
                        </MenuItem>
                      )}
                    </Select>
                    <FormHelperText>Target city for this local SEO landing page</FormHelperText>
                  </FormControl>
                </Grid>

                {/* Sub Category Selection (Filtered by Category) */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth disabled={!formData.category}>
                    <InputLabel>Sub Category (Optional)</InputLabel>
                    <Select
                      name="sub_category"
                      value={formData.sub_category || ''}
                      label="Sub Category (Optional)"
                      onChange={handleChange}
                      sx={{ borderRadius: 2 }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      <MenuItem value="">
                        <em>None (All Subcategories in {selectedCategoryName || 'Category'})</em>
                      </MenuItem>
                      {availableSubCategories.map((sub) => (
                        <MenuItem key={sub._id} value={sub.sub_category_name}>
                          {sub.sub_category_name}
                        </MenuItem>
                      ))}
                      {/* Preserve custom subcategory if previously entered */}
                      {formData.sub_category &&
                        !availableSubCategories.some((s) => s.sub_category_name === formData.sub_category) && (
                          <MenuItem value={formData.sub_category}>
                            {formData.sub_category} (Custom)
                          </MenuItem>
                        )}
                    </Select>
                    <FormHelperText>
                      {!formData.category
                        ? 'Select a Category first'
                        : availableSubCategories.length > 0
                        ? `${availableSubCategories.length} subcategories available for this category`
                        : 'No subcategories linked to this category'}
                    </FormHelperText>
                  </FormControl>
                </Grid>

                {/* Brand Selection (Filtered by Category) */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth disabled={!formData.category}>
                    <InputLabel>Brand (Optional)</InputLabel>
                    <Select
                      name="brand"
                      value={formData.brand || ''}
                      label="Brand (Optional)"
                      onChange={handleChange}
                      sx={{ borderRadius: 2 }}
                      MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
                    >
                      <MenuItem value="">
                        <em>None (All Brands in {selectedCategoryName || 'Category'})</em>
                      </MenuItem>
                      {availableBrands.map((b) => (
                        <MenuItem key={b._id} value={b.brand_name}>
                          {b.brand_name}
                        </MenuItem>
                      ))}
                      {/* Preserve custom brand if previously entered */}
                      {formData.brand && !availableBrands.some((b) => b.brand_name === formData.brand) && (
                        <MenuItem value={formData.brand}>
                          {formData.brand} (Custom)
                        </MenuItem>
                      )}
                    </Select>
                    <FormHelperText>
                      {!formData.category
                        ? 'Select a Category first'
                        : availableBrands.length > 0
                        ? `${availableBrands.length} brands available for this category`
                        : 'No brands linked to this category'}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* ── Section 2: SEO Meta Data & Google Preview ─────────────── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Tag size={18} color="#00AB55" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    SEO Metadata & Search Snippet
                  </Typography>
                </Box>
              </Box>

              {/* SERP Search Result Preview */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2.5,
                  mb: 3,
                  borderRadius: 2.5,
                  bgcolor: (theme) => alpha(theme.palette.background.neutral, 0.6),
                  border: '1px dashed',
                  borderColor: 'divider'
                }}
              >
                <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, display: 'block', mb: 1, letterSpacing: 0.5 }}>
                  GOOGLE SEARCH PREVIEW
                </Typography>
                <Typography variant="caption" sx={{ color: '#202124', fontSize: '0.8rem', display: 'block', mb: 0.5 }}>
                  https://www.resellerbazzar.in › {formData.buy_sell || 'buy'} › {formData.slug || 'page-slug'}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    color: '#1a0dab',
                    fontWeight: 600,
                    lineHeight: 1.3,
                    mb: 0.5,
                    cursor: 'pointer',
                    '&:hover': { textDecoration: 'underline' }
                  }}
                >
                  {formData.meta_title || formData.page_name || 'Page Title Appears Here | Reseller Bazzar'}
                </Typography>
                <Typography variant="body2" sx={{ color: '#4d5156', fontSize: '0.825rem', lineHeight: 1.4 }}>
                  {formData.meta_description || 'Page meta description appears here. Connect directly with verified suppliers and local dealers without intermediaries.'}
                </Typography>
              </Paper>

              <Grid container spacing={2.5}>
                {/* Meta Title */}
                <Grid size={{ xs: 12, sm: 7 }}>
                  <TextField
                    label="Meta Title"
                    name="meta_title"
                    value={formData.meta_title}
                    onChange={handleChange}
                    fullWidth
                    required
                    helperText={`${(formData.meta_title || '').length} / 60 characters (50-60 optimal)`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Keywords */}
                <Grid size={{ xs: 12, sm: 5 }}>
                  <TextField
                    label="Keywords"
                    name="keywords"
                    value={formData.keywords}
                    onChange={handleChange}
                    fullWidth
                    required
                    placeholder="e.g. laptops, buy laptops, ahmedabad"
                    helperText="Comma separated search keywords"
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>

                {/* Meta Description */}
                <Grid size={12}>
                  <TextField
                    label="Meta Description"
                    name="meta_description"
                    value={formData.meta_description}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    rows={3}
                    required
                    helperText={`${(formData.meta_description || '').length} / 160 characters (140-160 optimal)`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                  />
                </Grid>
              </Grid>
            </Box>

            <Divider />

            {/* ── Section 3: Dynamic Meta Content Blocks ────────────────── */}
            <Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <AlignLeft size={18} color="#FE5F01" />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    Page Meta Content Blocks
                  </Typography>
                </Box>
                <Button
                  size="small"
                  startIcon={<Plus size={16} />}
                  onClick={addMetaContent}
                  variant="outlined"
                  sx={{ borderRadius: 2, fontWeight: 700, textTransform: 'none' }}
                >
                  Add Block
                </Button>
              </Box>

              {formData.meta_content.length === 0 ? (
                <Box
                  sx={{
                    p: 3,
                    bgcolor: 'background.neutral',
                    borderRadius: 2.5,
                    border: '1px dashed',
                    borderColor: 'divider',
                    textAlign: 'center'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No custom keyword blocks added yet. Click <strong>"Add Block"</strong> to insert dynamic SEO content, FAQs, or keyword descriptions into this landing page.
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={2}>
                  {formData.meta_content.map((block, index) => (
                    <Box
                      key={index}
                      sx={{
                        p: 2.5,
                        borderRadius: 2.5,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'background.paper',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
                        position: 'relative'
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Chip
                          size="small"
                          label={`CONTENT BLOCK #${index + 1}`}
                          sx={{ height: 20, fontSize: 10, fontWeight: 800, bgcolor: 'background.neutral', color: 'text.secondary' }}
                        />
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => removeMetaContent(index)}
                          sx={{ bgcolor: alpha('#ef4444', 0.08), '&:hover': { bgcolor: alpha('#ef4444', 0.16) } }}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </Box>

                      <Grid container spacing={2}>
                        <Grid size={12}>
                          <TextField
                            label="Keyword Title"
                            value={block.keyword_title}
                            onChange={(e) => handleMetaContentChange(index, 'keyword_title', e.target.value)}
                            fullWidth
                            required
                            placeholder="e.g. Why buy refurbished laptops in Ahmedabad?"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        </Grid>
                        <Grid size={12}>
                          <TextField
                            label="Keyword Description"
                            value={block.keyword_description}
                            onChange={(e) => handleMetaContentChange(index, 'keyword_description', e.target.value)}
                            fullWidth
                            multiline
                            rows={3}
                            required
                            placeholder="Detailed keyword description, buying guide or product insights..."
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>
          </Box>
        </AdminDialogContent>

        {/* Footer Actions */}
        <AdminDialogActions sx={{ justifyContent: 'space-between' }}>
          <Button onClick={onClose} color="inherit" disabled={loading} sx={{ fontWeight: 600 }}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={16} color="inherit" />}
            sx={{
              px: 3.5,
              py: 1,
              fontWeight: 700,
              borderRadius: 2,
            }}
          >
            {loading ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Page'}
          </Button>
        </AdminDialogActions>
      </form>
    </AdminDialog>
  );
};

export default CustomPageModal;
